<script setup lang="ts">
import type { TableOptions } from '@core/stores/user'
import type { SortItem } from '@core/types'
import { useReportFilters } from '@/composables/useReportFilters'
import ReportFiltersBar from '@/views/pages/reports/ReportFiltersBar.vue'

const { t } = useI18n()
const table = 'analytics-page.tours'

definePage({
  meta: {
    action: 'read',
    subject: 'analytics',
  },
})

const headers = [
  { title: t(`${table}-page.full_name`), key: 'full_name' },
  { title: t(`${table}-page.team`), key: 'team' },
  { title: t(`${table}-page.games_count`), key: 'games_count' },
  { title: t(`${table}-page.effectiveness`), key: 'total' },
]

interface Item {
  id: string
  title: string
}
const { data: toursList } = await useApi<any>('/tours/all')
let tours: Item[] = []
if (toursList?.value)
  tours = toursList.value

const defaultTableOptions: TableOptions = {
  sortBy: 'id',
  orderBy: 'asc',
  page: 1,
  perPage: 10,
}

const cookieTableOptions = useCookie<TableOptions>(`${table}TableOptions`, {
  default: () => defaultTableOptions,
})

let currentTableOptions: TableOptions | undefined = cookieTableOptions.value

if (currentTableOptions === undefined) {
  currentTableOptions = defaultTableOptions

  const newTableOptions: any = cookieTableOptions.value

  newTableOptions.push(currentTableOptions)

  useCookie(`${table}TableOptions`).value = newTableOptions
}

const searchQuery = ref('')
const debouncedSearch = useDebounce(searchQuery, 300)
const itemsPerPage = ref(currentTableOptions.perPage)
const selectedTour = ref(currentTableOptions.tour)
const page = ref(currentTableOptions.page)
const sortBy = ref<string>(currentTableOptions.sortBy)
const orderBy = ref<undefined | boolean | 'asc' | 'desc'>(currentTableOptions.orderBy)
const currenItem = ref()
const isLoading = ref(false)
const isAddDialogOpen = ref(false)
const isDeleteConfirmDialogVisible = ref(false)

const updateOptions = (options: any) => {
  page.value = options.page
  if (options.sortBy[0] !== undefined) {
    sortBy.value = options.sortBy[0]?.key
    orderBy.value = options.sortBy[0]?.order
  }
  else {
    sortBy.value = defaultTableOptions.sortBy
    orderBy.value = defaultTableOptions.orderBy
  }
}

const sortTable: SortItem[] = [{ key: sortBy.value, order: orderBy.value }]

const reportFilters = useReportFilters({ fields: ['departments', 'positions', 'employees', 'scenarios'] })

// Sync, so the table refetches once with page 1 instead of first with the old page.
watch(reportFilters.filters, () => {
  page.value = 1
}, { flush: 'sync' })

const getExportUrl = () => {
  return createUrl('/api/analytics/tours/export',
    {
      query: () => ({
        search: debouncedSearch,
        page,
        itemsPerPage,
        sortBy,
        orderBy,
        tour_id: selectedTour,
        ...reportFilters.query.value,
      }),
    },
  ).value
}

const { data: itemsData } = await useApi<any>(createUrl('/analytics/tours',
  {
    query: () => ({
      search: debouncedSearch,
      page,
      itemsPerPage,
      sortBy,
      orderBy,
      tour_id: selectedTour,
      ...reportFilters.query.value,
    }),
  },
))

const items = computed(() => itemsData.value?.data)

const total = computed(() => itemsData.value?.total)

watch([
  isDeleteConfirmDialogVisible, isAddDialogOpen,
], (val, old) => {
  if ((val[0] === false && old[0] === true) || (val[1] === false && old[1] === true))
    setTimeout(() => currenItem.value = undefined, 200)
})

watch([page, itemsPerPage, sortBy, orderBy, selectedTour], (val, old) => {
  if (val[1] !== old[1])
    page.value = 1

  useCookie(`${table}TableOptions`).value = JSON.stringify({
    page: val[0],
    perPage: val[1],
    sortBy: val[2] === undefined ? currentTableOptions?.sortBy : val[2],
    orderBy: val[3] === undefined ? currentTableOptions?.orderBy : val[3],
    tour: val[4] === undefined ? currentTableOptions?.tour : val[4],
  })
})
</script>

<template>
  <div class="d-flex flex-column gap-6">
    <ReportFiltersBar :report-filters="reportFilters" />
    <VCard>
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div class="me-3 d-flex gap-3">
          <VTextField
            v-model="searchQuery"
            clearable
            :placeholder="t('search')"
            style=" min-inline-size: 200px;"
          />
        </div>
        <AppSelect
          v-model="selectedTour"
          clear-icon="tabler-x"
          :items="tours"
          item-title="title"
          item-value="id"
          style=" min-inline-size: 200px;"
          :placeholder="t(`${table}-page.tour`)"
        />
        <VSpacer />
        <div class="d-flex align-center flex-wrap gap-4">
          <VBtn
            variant="tonal"
            color="secondary"
            prepend-icon="tabler-upload"
            :href="getExportUrl()"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ t(`${table}-page.export`) }}
          </VBtn>
        </div>
      </VCardText>
      <div class="category-table">
        <VDataTableServer
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :headers="headers"
          :sort-by="sortTable"
          :loading="isLoading"
          :loading-text="t('loading')"
          :items="items"
          :items-length="total"
          class="text-no-wrap"
          @update:options="updateOptions"
        >
          <template #item.full_name="{ item }">
            {{ item.full_name }}
          </template>
          <template #item.team="{ item }">
            {{ item.team }}
          </template>
          <template #item.total="{ item }">
            {{ item.total }}
          </template>
          <template #bottom>
            <div>
              <VDivider />

              <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 px-6 py-3">
                <AppSelect
                  v-model="itemsPerPage"
                  density="compact"
                  class="app-select flex-grow-0"
                  :items="[5, 10, 25, 50]"
                  style="max-inline-size: 5rem;min-inline-size: 5rem;"
                />

                <VPagination
                  v-model="page"
                  active-color="primary"
                  :length="Math.ceil(total / itemsPerPage)"
                  :total-visible="$vuetify.display.xs ? 1 : Math.min(Math.ceil(total / itemsPerPage), 5)"
                />
              </div>
            </div>
          </template>
        </VDataTableServer>
      </div>
    </VCard>
  </div>
</template>

<style lang="scss">
.ProseMirror-focused{
  border: none;
}
</style>
